import { checkIfGateIsSingular, isOwnerOfSession } from './utilGates';
import {
  ConsentPolicyType,
  LatestEventType,
  ParkingSession,
  ParkingType,
  ParkingTypeEnum,
  UserType,
} from './utilTypes';
import {
  isUserIdentityVerified,
  isUserParkingRequestApproved,
} from './utilsUser';

export const isRequestInexistent = (
  user: UserType | null,
  parking: ParkingType
) => {
  return (
    parking.requiresApproval && !isUserParkingRequestApproved(user, parking)
  );
};

export const isRequestPendingApproval = (parking: ParkingType) => {
  return parking?.requiresApproval && parking?.status === 'pending';
};

export const isIDValidationCase = (
  user: UserType | null,
  parking: ParkingType
) => {
  return parking.requiresIdVerification && !isUserIdentityVerified(user);
};

const computeAgeClient = (dateOfBirth: string): number => {
  const today = new Date();
  const dob = new Date(dateOfBirth);
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
};

export const getEligibilityDate = (dateOfBirth: string, minimumAge: number): string => {
  const dob = new Date(dateOfBirth);
  const eligible = new Date(dob.getFullYear() + minimumAge, dob.getMonth(), dob.getDate());
  return eligible.toLocaleDateString();
};

export const getEffectiveLegalAge = (parking: ParkingType): number | null => {
  const agePolicy = parking.referenceParking ?? parking;
  return agePolicy?.legalAge ?? null;
};

export const isAgeRestrictionCase = (
  user: UserType | null,
  parking: ParkingType
): boolean => {
  const minAge = getEffectiveLegalAge(parking);
  if (minAge == null) return false;
  if (!user?.dateOfBirth) return true;
  const age = computeAgeClient(user.dateOfBirth);
  return Number.isNaN(age) || age < minAge;
};

export const getRequiredConsentPolicies = (
  parking: ParkingType
): ConsentPolicyType[] => {
  const consentSource = parking.referenceParking ?? parking;
  return consentSource?.consentPolicies ?? [];
};

export const getUnacceptedConsentPolicies = (
  user: UserType | null,
  parking: ParkingType
): ConsentPolicyType[] => {
  const requiredPolicies = getRequiredConsentPolicies(parking);
  if (requiredPolicies.length === 0) return [];

  const acceptedIds = new Set(
    (user?.consents ?? [])
      .map((c) => c.policy?.id)
      .filter((id): id is string => Boolean(id)),
  );
  return requiredPolicies.filter((policy) => !acceptedIds.has(policy.id));
};

export const isConsentRequiredCase = (
  user: UserType | null,
  parking: ParkingType
): boolean => getUnacceptedConsentPolicies(user, parking).length > 0;

export const getParkingOrganizationId = (parking: ParkingType): string | null =>
  parking.organization?.id ?? parking.referenceParking?.organization?.id ?? null;

export const getParkingOrganizationName = (parking: ParkingType): string | null =>
  parking.organizationName ??
  parking.referenceParking?.organizationName ??
  parking.organization?.name ??
  parking.referenceParking?.organization?.name ??
  null;

export const getParkingOrganization = (
  parking: ParkingType
): { id: string; name: string } | null => {
  const name = getParkingOrganizationName(parking);
  const id = getParkingOrganizationId(parking);
  if (!name) return null;
  return { id: id ?? '', name };
};

export const isOrgAccessRestricted = (
  user: UserType | null,
  parking: ParkingType
): boolean => {
  const orgId = getParkingOrganizationId(parking);
  if (!orgId || !user?.accessRestrictions?.length) return false;

  return user.accessRestrictions.some(
    (restriction) =>
      restriction.organization?.id === orgId &&
      restriction.reason === 'manager_ban'
  );
};

export const isSubscriptionCase = (
  user: UserType | null,
  parking: ParkingType,
  userSubscriptions: any[] = []
) => {
  if (!user || parking.subscriptionPlans?.length === 0) {
    return false;
  }

  const hasActiveSubscription = parking.subscriptionPlans.some((plan) => {
    const foundMatchingSubscription = userSubscriptions.find(
      (s) => s.metadata.subscriptionPlanId === plan.id
    );

    if (!foundMatchingSubscription) {
      return false;
    }

    return foundMatchingSubscription.status === 'active';
  });

  return !hasActiveSubscription;
};

export const getActiveSessionFromLatestEvent = (
  event: LatestEventType,
  currentUser: UserType | null
) => {
  if (!event || !event.gate || !event.gate.sessions) return null;

  const sessions = event.gate.sessions.filter((session) => {
    return (
      session.endedAt === null &&
      isOwnerOfSession(session, currentUser) &&
      new Date(session.startedAt) < new Date()
    );
  });

  return sessions[sessions.length - 1] || null;
};

export const getLabelsForParkingType = (
  t: any,
  type: ParkingTypeEnum
): Record<'lock' | 'unlock' | 'title' | 'description', string | null> => {
  const DEFAULT_LABELS = {
    unlock: t('parkCase'),
    lock: t('pickUpCase'),
    title: t('gatesAreOpenTitle'),
    description: t('gatesAreOpenDescription'),
  };

  const labels: Record<
    ParkingTypeEnum,
    Record<'lock' | 'unlock' | 'title' | 'description', string | null>
  > = {
    parking: {
      unlock: t('parkCase'),
      lock: t('pickUpCase'),
      title: t('gatesAreOpenTitle'),
      description: t('gatesAreOpenDescription'),
    },
    rental: {
      unlock: t('rentalStartCase'),
      lock: t('rentalEndCase'),
      title: t('chainIsOpenTitle'),
      description: null,
    },
  };

  if (!labels[type]) {
    return DEFAULT_LABELS;
  }

  return labels[type];
};

export const checkIfUserUpcomingSession = (
  session: ParkingSession
): boolean => {
  if (!session) return false;

  const sessionStart = new Date(session.startedAt);
  const now = new Date();

  return sessionStart > now;
};

// Maps a stable GraphQL error `extensions.code` (see unlock.ts) to a locale key,
// so the backend's English-only Error#message never leaks into a localized UI.
const UNLOCK_ERROR_CODE_KEYS: Record<string, string> = {
  CONSENT_REQUIRED: 'unlockErrors.consentRequired',
};

export const getUnlockErrorMessage = (
  t: (key: string) => string,
  error?: { message: string; graphQLErrors?: readonly { extensions?: { code?: string } }[] } | null
): string | null => {
  if (!error) return null;

  const code = error.graphQLErrors?.[0]?.extensions?.code;
  if (code && UNLOCK_ERROR_CODE_KEYS[code]) {
    return t(UNLOCK_ERROR_CODE_KEYS[code]);
  }

  return error.message;
};
